import type { WarrantyEvidenceStatus } from '../types'

/** Statusi izvora koji uvijek traže potvrdu korisnika prije nego se prikažu kao činjenica. */
export const EVIDENCE_REQUIRING_CONFIRMATION: WarrantyEvidenceStatus[] = ['estimated', 'unknown']

export function requiresUserConfirmation(status: WarrantyEvidenceStatus): boolean {
  return EVIDENCE_REQUIRING_CONFIRMATION.includes(status)
}

export function isConfirmedEvidence(status: WarrantyEvidenceStatus): boolean {
  return !requiresUserConfirmation(status)
}

export const EVIDENCE_LABELS: Record<WarrantyEvidenceStatus, string> = {
  document_confirmed: 'Potvrđeno dokumentom',
  merchant_confirmed: 'Potvrđeno kod trgovca',
  manufacturer_confirmed: 'Potvrđeno kod proizvođača',
  legal_rule: 'Izračunato prema zakonskom pravilu',
  user_entered: 'Uneseno ručno',
  estimated: 'Procijenjeno — provjerite',
  unknown: 'Nije poznato',
}

export function evidenceLabel(status: WarrantyEvidenceStatus): string {
  return EVIDENCE_LABELS[status] ?? EVIDENCE_LABELS.unknown
}

const KNOWN_STATUSES = new Set<string>(Object.keys(EVIDENCE_LABELS))

/**
 * Mapira slobodan tekst iz AI odgovora u kontrolirani skup statusa.
 * Sve što ne prepoznamo namjerno pada na 'unknown', nikad na 'document_confirmed'.
 */
export function mapEvidenceStatus(raw: unknown): WarrantyEvidenceStatus {
  if (typeof raw !== 'string') return 'unknown'
  const value = raw.trim().toLowerCase().replace(/[\s-]+/g, '_')
  if (KNOWN_STATUSES.has(value)) return value as WarrantyEvidenceStatus
  switch (value) {
    case 'document':
    case 'receipt':
    case 'on_receipt':
    case 'warranty_card':
      return 'document_confirmed'
    case 'merchant':
    case 'retailer':
    case 'seller':
      return 'merchant_confirmed'
    case 'manufacturer':
    case 'brand':
      return 'manufacturer_confirmed'
    case 'law':
    case 'legal':
    case 'statutory':
      return 'legal_rule'
    case 'manual':
    case 'user':
      return 'user_entered'
    case 'guess':
    case 'assumed':
    case 'typical':
    case 'inferred':
      return 'estimated'
    default:
      return 'unknown'
  }
}

/**
 * Ako AI tvrdi visoku pouzdanost, a izvor nije provjerljiv, status se degradira.
 * Model nikad ne smije prikazati izmišljeno trajanje kao potvrđenu činjenicu.
 */
export function reconcileEvidence(
  status: WarrantyEvidenceStatus,
  confidence: number,
  hasExplicitDocumentMention: boolean,
): WarrantyEvidenceStatus {
  if (status === 'document_confirmed' && !hasExplicitDocumentMention) return 'estimated'
  if (isConfirmedEvidence(status) && status !== 'legal_rule' && confidence < 0.6) return 'estimated'
  return status
}
